import { useState } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { createChainedFunction } from '@atc/create-chained-function';
import { useSelector } from 'react-redux';
import { ratesLabels } from 'axl-config';
import { Text, LinkIcon, Button, Link } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { textLinkDealClick, editThisOfferClick, seeDetailsDealClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import DigitalRetailOverlayCTA from '../DigitalRetailOverlayCTA/DigitalRetailOverlayCTA.js';
import EsntialBuyNowCTA from '../EsntialBuyNowCTA/EsntialBuyNowCTA.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
const pipe = /*#__PURE__*/jsx("span", {
  className: "padding-horizontal-2",
  children: "|"
});
const renderDeepLink = function () {
  let {
    creditApp,
    onSDPError,
    sdpLinkText,
    sdpRoute,
    vehicle,
    ...rest
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    creditAppSubmittedFlag
  } = creditApp;
  const route = creditAppSubmittedFlag ? 'dashboard' : sdpRoute;
  return /*#__PURE__*/jsx(DigitalRetailOverlayCTA, {
    buttonText: sdpLinkText,
    listedPrice: vehicle.sellingPrice,
    listing: vehicle,
    onError: onSDPError,
    uiContext: "dealPanel",
    route: route,
    ...rest
  });
};
const renderDetails = function () {
  let items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let showEditButton = arguments.length > 1 ? arguments[1] : undefined;
  let vdpUrl = arguments.length > 2 ? arguments[2] : undefined;
  let onCTAClick = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : () => {};
  return /*#__PURE__*/jsxs(Fragment, {
    children: [items.map(_ref => {
      let {
        title,
        value,
        shouldRender = true
      } = _ref;
      return shouldRender && /*#__PURE__*/jsxs("div", {
        className: "display-flex justify-content-between padding-vertical-1",
        children: [/*#__PURE__*/jsx(Text, {
          weight: "bold",
          children: title
        }), /*#__PURE__*/jsx("div", {
          className: "text-right",
          "data-cmp": title,
          children: /*#__PURE__*/jsx(Text, {
            children: value()
          })
        })]
      }, title);
    }), showEditButton && /*#__PURE__*/jsx(Button, {
      className: "margin-vertical-4",
      block: true,
      href: vdpUrl,
      onClick: onCTAClick,
      children: "Edit This Offer"
    })]
  });
};
const getItems = _ref2 => {
  let {
    creditApp,
    dealId,
    offerDetails,
    onSDPError,
    paymentsDuck,
    tradeIn,
    vehicle,
    isAccelerate,
    vdpUrl,
    onCTAClick,
    isReceiptView,
    isEsntialDeal,
    ...rest
  } = _ref2;
  const {
    creditRating,
    loanTerm,
    rate,
    downPayment,
    leaseDownPayment,
    dealType,
    adjustedCapCost,
    mileageLimit,
    payment
  } = offerDetails;
  const {
    negotiation
  } = vehicle;
  const isCashDeal = dealType?.toLowerCase() === 'cash';
  const isLeaseDeal = dealType?.toLowerCase() === 'lease';
  const downPaymentValue = isLeaseDeal ? leaseDownPayment : downPayment;
  const {
    creditAppSubmittedFlag
  } = creditApp;
  const linkProps = {
    creditApp,
    vehicle,
    paymentsDuck,
    onSDPError,
    isReceiptView,
    ...rest
  };
  const isDownPaymentLocked = creditAppSubmittedFlag && !downPaymentValue;
  const isTradeInLocked = creditAppSubmittedFlag && !tradeIn?.netTrade;
  const downPaymentLabel = downPaymentValue <= 0 || isDownPaymentLocked ? '' : formatCurrency(downPaymentValue);
  const tradeInLabel = isTradeInLocked ? '' : formatCurrency(tradeIn?.netTrade);
  const itemValue = _ref3 => {
    let {
      label,
      disableLink,
      propsForDeepLink = {},
      nonDrLinkText = '',
      showPipe = true,
      hideNonDRLinkText
    } = _ref3;
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        children: label
      }), !disableLink && isAccelerate && /*#__PURE__*/jsxs(Fragment, {
        children: [showPipe && pipe, renderDeepLink({
          ...linkProps,
          ...propsForDeepLink,
          dealId
        })]
      }), hideNonDRLinkText ? '---' : /*#__PURE__*/jsxs(Fragment, {
        children: [!isAccelerate && nonDrLinkText && !isReceiptView && /*#__PURE__*/jsx(Link, {
          href: vdpUrl,
          onClick: event => onCTAClick(event, nonDrLinkText, propsForDeepLink.placement),
          undecorated: true,
          children: nonDrLinkText
        }), !isAccelerate && nonDrLinkText && isReceiptView && /*#__PURE__*/jsx(Button, {
          bsStyle: "text",
          onClick: event => onCTAClick(event, nonDrLinkText, propsForDeepLink.placement),
          children: nonDrLinkText
        })]
      })]
    });
  };
  return [{
    title: isEsntialDeal && 'Vehicle Price' || (!isCashDeal ? 'Offer Price' : 'Estimated Cash Payment'),
    shouldRender: vehicle.offerPrice,
    // If dealer has negotiation setting, then add the Edit/View links
    value: () => itemValue({
      label: formatCurrency(vehicle.offerPrice),
      disableLink: negotiation
    })
  }, {
    title: 'Credit Rating',
    shouldRender: !isCashDeal,
    value: () => itemValue({
      label: ratesLabels[creditRating]?.label,
      propsForDeepLink: {
        placement: 'Credit Rating'
      },
      disableLink: false
    })
  }, {
    title: 'Loan Terms',
    shouldRender: !isCashDeal && !isLeaseDeal,
    value: () => itemValue({
      label: `${loanTerm} mo. at ${rate}% APR`,
      propsForDeepLink: {
        placement: 'Loan Terms'
      },
      disableLink: false
    })
  }, {
    title: 'Lease Term',
    shouldRender: isLeaseDeal,
    value: () => itemValue({
      label: `${loanTerm} mo.`,
      disableLink: false
    })
  }, {
    title: isLeaseDeal ? 'Due At Signing' : 'Cash Down',
    shouldRender: !isCashDeal,
    value: () => itemValue({
      label: downPaymentValue > 0 && downPaymentLabel,
      disableLink: false,
      propsForDeepLink: {
        sdpLinkText: downPaymentValue ? '' : 'Add Down Payment',
        placement: isLeaseDeal ? 'Due At Signing' : 'Cash Down'
      },
      nonDrLinkText: (downPaymentValue <= 0 || !downPaymentValue) && 'Add Down Payment',
      hideNonDRLinkText: isEsntialDeal && (downPaymentValue <= 0 || !downPaymentValue),
      showPipe: !!downPaymentValue && downPaymentValue > 0
    })
  }, {
    title: 'Miles Per Year',
    shouldRender: isLeaseDeal,
    value: () => itemValue({
      label: mileageLimit,
      disableLink: false
    })
  }, {
    title: 'Est. Adjusted Cap Cost',
    shouldRender: isLeaseDeal,
    value: () => itemValue({
      label: adjustedCapCost > 0 && formatCurrency(adjustedCapCost),
      disableLink: false
    })
  }, {
    title: 'Trade-In Value',
    shouldRender: true,
    // Always true
    value: () => itemValue({
      label: !!tradeIn?.netTrade && tradeInLabel,
      disableLink: false,
      propsForDeepLink: {
        sdpLinkText: tradeIn?.netTrade ? '' : 'Value Your Vehicle',
        sdpRoute: 'tradeInManualEntry',
        placement: 'Trade-In Value'
      },
      nonDrLinkText: !tradeIn?.netTrade && 'Value Your Vehicle',
      hideNonDRLinkText: isEsntialDeal && !tradeIn?.netTrade,
      showPipe: !!tradeIn?.netTrade
    })
  }, {
    title: 'Est. Monthly Payment',
    shouldRender: isReceiptView && !!payment,
    value: () => itemValue({
      label: formatCurrency(Math.round(payment)),
      disableLink: true
    })
  }];
};
function DealCardOfferDetails(_ref4) {
  let {
    dealsDuck,
    paymentsDuck,
    isAccelerate,
    vdpUrl,
    isReceiptView = false,
    onClick,
    handleDealReceiptOverride,
    isEsntialDeal = false,
    onEmailCTAClick,
    ...rest
  } = _ref4;
  const {
    vehicle
  } = rest;
  const paymentsInfo = useSelector(paymentsDuck.selectors.getDuckState);
  const {
    ecommWebsite,
    zip
  } = vehicle;
  const [showDetails, setShowDetails] = useState(isReceiptView);
  const fireEditDealerOfferCTAClick = (event, buttonText, placement) => {
    if (isReceiptView) {
      handleDealReceiptOverride();
      sendClick(textLinkDealClick, event, {
        text: buttonText,
        inventoryId: vehicle?.id,
        placement
      });
    } else {
      sendClick(editThisOfferClick, event, {
        inventoryId: vehicle.id
      });
    }
  };
  const onEditDealCTAClick = createChainedFunction(onClick, fireEditDealerOfferCTAClick);
  const items = getItems({
    ...rest,
    paymentsInfo,
    paymentsDuck,
    isAccelerate,
    vdpUrl,
    onCTAClick: onEditDealCTAClick,
    isReceiptView,
    isEsntialDeal
  });
  const renderedItemsCount = items.filter(item => item.shouldRender).length;
  if (renderedItemsCount === 1) {
    return /*#__PURE__*/jsxs(Fragment, {
      children: [renderDetails(items, !isAccelerate && !isReceiptView && !isEsntialDeal, vdpUrl, onEditDealCTAClick), isAccelerate && /*#__PURE__*/jsx("hr", {})]
    });
  }
  const onSeeDetailsClick = event => {
    setShowDetails(state => !state);
    sendClick(seeDetailsDealClick, event, {
      inventoryId: vehicle.id
    });
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isReceiptView && /*#__PURE__*/jsxs("div", {
      className: "display-flex justify-content-between",
      children: [/*#__PURE__*/jsx(Text, {
        weight: "bold",
        children: "Offer"
      }), /*#__PURE__*/jsx("div", {
        className: "text-right",
        children: renderedItemsCount > 1 && /*#__PURE__*/jsx(LinkIcon, {
          onClick: onSeeDetailsClick,
          glyph: showDetails ? 'menu-up' : 'menu-down',
          placement: "after",
          undecorated: true,
          children: "See Details"
        })
      })]
    }), showDetails && renderDetails(items, !isAccelerate && !isReceiptView && !isEsntialDeal, vdpUrl, onEditDealCTAClick), !isReceiptView && /*#__PURE__*/jsx("hr", {}), isEsntialDeal && /*#__PURE__*/jsx(EsntialBuyNowCTA, {
      href: ecommWebsite?.href,
      listing: vehicle // vehicle has a limited details of listing.
      ,

      label: "Continue Your Order",
      analyticsData: {
        listingId: vehicle?.id,
        placement: isReceiptView ? 'receipt' : 'deal card' // Needs tagging update as per comp
      },
      bsStyle: "primary",
      className: "margin-top-3",
      dealsDuck: dealsDuck,
      paymentsDuck: paymentsDuck,
      zip: zip || vehicle?.dealer?.location?.address?.zip,
      block: true
    }), !!onEmailCTAClick && /*#__PURE__*/jsx(EmailCTA, {
      listingId: vehicle?.id,
      type: "block",
      labelKey: "contactDealer",
      showIcon: false,
      className: "margin-top-5",
      onClick: onEmailCTAClick,
      buttonStyle: "primary",
      context: "availableOffers"
    })]
  });
}
export { DealCardOfferDetails as default };
